import { GetDeal_deal } from '../../graphql/queries/__generated__/GetDeal';
import {
  DataLayerEvent,
  DataLayerFormName,
  DataLayerFormStage,
  DataLayerType,
  DxpDataLayerEventNames,
} from '../../../../../../common/utilities/customDatalayerInterface';
import dxpDigitalDataAssignment from '../../../../../../common/utilities/dxpDigitalDataAssignment';
import { formatDataLayerDeal, getCheckoutSteps } from '../dxpDataLayerHandler';
import dxpDataLayerToggleVerification from '../../../../../../common/utilities/dxpDataLayerToggleVerification';
import { IStep } from '../../../checkout4/model/IJourneySteps';
import { INavigationStep } from '../../../checkout3/common/model/INavigationSteps';
import { JourneyStepsRoutes } from '../../../checkout4/enum/JourneyStepsRoutes';
import dxpProducts from '../../../../../../common/constants/dxpProducts';
import dataLayerToggleVerification from '../../../../../../common/utilities/dataLayerToggleVerification';
import IDimensions from '../../../../../../common/model/IDimensions';
import digitalDataAssignment from '../../../../../../common/utilities/digitalDataAssignment';
import buildSelectedSpecData from './buildSelectedSpecData';
import { customerStorage } from '../../../../../../common/service/customerStorage';
import hashUtilities from '../../../../../../common/utilities/hashUtilities';
import { FinanceResultType, JourneyType } from '../../../../__generated__/globalTypes';

interface ConfirmationScreenDataLayerHandlerArgs {
  deal: GetDeal_deal;
  activeStep?: number;
  steps?: Array<IStep | INavigationStep>;
  dimensions?: IDimensions;
}

const ConfirmationScreenDataLayerHandler = async ({
  deal,
  activeStep,
  steps,
  dimensions,
}: ConfirmationScreenDataLayerHandlerArgs): Promise<void> => {
  try {
    const isDataLayer = dataLayerToggleVerification(dimensions);
    const isDxpDataLayer = dxpDataLayerToggleVerification();
    const isOnlineOrder = deal.journeyType === JourneyType.online_order;

    if (isDataLayer) {
      const { email, phone } = deal.customer;
      const { postcode } = customerStorage.getCustomerData(dimensions) || {};
      const dealer = deal?.dealer;
      const emailHashed = await hashUtilities.hashSHA256(email);
      const phoneHashed = await hashUtilities.hashSHA256(phone);

      if (isOnlineOrder) {
        digitalDataAssignment({
          event: DataLayerEvent.FORM_NAVIGATE,
          form: {
            name: DataLayerFormName.CONFIGURATOR_STOCK_RESERVATION,
            stage: DataLayerFormStage.FINALISE_TRANSACTION,
            details: {
              configurator: {
                financeOption: deal.finance.type as FinanceResultType,
                selectedSpec: buildSelectedSpecData({ deal, dimensions }),
              },
            },
          },
          user: {
            emailHashed: emailHashed || null,
            phoneHashed: phoneHashed || null,
            postCode: postcode || null,
            preferredDealership: dealer?.label || null,
          },
        } as DataLayerType);
      } else {
        digitalDataAssignment({
          event: DataLayerEvent.FORM_NAVIGATE,
          form: {
            name: DataLayerFormName.CONFIGURATOR,
            stage: DataLayerFormStage.CHECKOUT_CONFIRMATION,
            details: {
              configurator: {
                dealId: deal.uuid,
                selectedSpec: buildSelectedSpecData({ deal, dimensions }),
              },
            },
          },
          user: {
            postCode: postcode || null,
            emailHashed: emailHashed || null,
            preferredDealership: dealer?.label || null,
          },
        });
      }
    }

    if (isDxpDataLayer) {
      const {
        car: {
          prices: {
            carPrice: { originalPrice },
            totalPrice,
            totalFeatures,
          },
        },
        finance,
        dealer,
      } = deal;
      const simulation = finance?.simulation;

      dxpDigitalDataAssignment({
        eventName: DxpDataLayerEventNames.CHECKOUT_LEAD_QUOTE,
        ...formatDataLayerDeal(deal),
        pageTitle: document.title,
        ...(steps ? getCheckoutSteps(steps, activeStep) : {}),
        stepName: JourneyStepsRoutes.CONFIRMATION.replace('/', ''),
        paymentMethod: finance?.type,
        creditType: simulation?.financeProductName,
        vehiclePrice: originalPrice?.[0]?.money?.amount,
        accessoriesPrice: totalFeatures?.[0]?.money?.amount,
        totalPrice: totalPrice?.[0]?.money?.amount,
        downPayment: simulation?.deposit?.[0]?.amount,
        downPaymentPercentage: simulation?.attributes?.depositAmountPercentage,
        paymentPeriod: simulation?.loanTermPeriod?.value,
        financeAmount: simulation?.amountPayable?.[0]?.amount,
        residualValue: simulation?.attributes?.residualAmount?.[0]?.amount,
        flatRate: simulation?.interestRate,
        interestRate: simulation?.interestRate,
        comparisonRate: simulation?.comparisonRate,
        monthlyPayment: simulation?.paymentValue?.[0]?.amount,
        dealerPreference: dealer?.label,
        DXPProduct: dxpProducts.checkout,
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('DataLayer error', err);
  }
};

export default ConfirmationScreenDataLayerHandler;
